import { useState } from 'react';
import { toast } from 'sonner';
import { Calendar, Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { getDaysUntilInterview, updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import DatePicker from '@/components/ui/DatePicker';
import ErrorAlert from '@/components/ui/ErrorAlert';

type Mode = 'date' | 'none';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function EditableInterviewDateRow() {
  const { profile, user, refreshProfile } = useAuth();
  const current = profile?.interview_date ?? null;
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<Mode>(current ? 'date' : 'none');
  const [date, setDate] = useState(current ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const draftValue = mode === 'date' && date ? date : null;
  const unchanged = draftValue === current;
  const canSave = !unchanged && !(mode === 'date' && !date) && !saving;

  const daysUntil = getDaysUntilInterview(current);
  const draftDaysUntil = mode === 'date' && date ? getDaysUntilInterview(date) : null;

  const startEdit = () => {
    setMode(current ? 'date' : 'none');
    setDate(current ?? '');
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setMode(current ? 'date' : 'none');
    setDate(current ?? '');
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    if (!user?.id || !canSave) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { interview_date: draftValue });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    await refreshProfile();
    track('profile_updated', { field: 'interview_date' });
    setSaving(false);
    setEditing(false);
    toast.success('Saved');
  };

  const cardClass = (active: boolean) =>
    `w-full text-left p-4 rounded-xl border-2 transition-colors cursor-pointer ${
      active
        ? 'border-primary bg-primary-light'
        : 'border-border bg-card hover:border-primary/30'
    }`;

  return (
    <Card>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <Calendar size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-sans text-sm text-text-muted">Interview Date</p>
          <p className="font-sans font-semibold text-base text-text-primary">
            {formatDate(current)}
          </p>
          {daysUntil !== null && (
            <p className="font-sans text-xs text-text-secondary mt-0.5">
              {daysUntil > 0
                ? `${daysUntil} day${daysUntil !== 1 ? 's' : ''} remaining`
                : daysUntil === 0
                  ? 'Today!'
                  : 'Past date'}
            </p>
          )}
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            aria-label="Edit interview date"
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:bg-cream-warm transition-colors cursor-pointer"
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-5">
          <div role="radiogroup" aria-label="Interview date" className="space-y-3">
            <div
              role="radio"
              aria-checked={mode === 'date'}
              tabIndex={0}
              onClick={() => setMode('date')}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setMode('date');
                }
              }}
              className={cardClass(mode === 'date')}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    mode === 'date' ? 'border-primary' : 'border-border'
                  }`}
                >
                  {mode === 'date' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-sans font-medium text-text-primary">
                  Select a date
                </span>
              </div>
              {mode === 'date' && (
                <div
                  className="mt-3 ml-0 sm:ml-8"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <DatePicker value={date || null} onChange={setDate} />
                  {draftDaysUntil !== null && draftDaysUntil > 0 && (
                    <p className="mt-3 font-sans text-sm text-success font-medium">
                      {draftDaysUntil} {draftDaysUntil === 1 ? 'day' : 'days'} away
                    </p>
                  )}
                </div>
              )}
            </div>

            <div
              role="radio"
              aria-checked={mode === 'none'}
              tabIndex={0}
              onClick={() => {
                setMode('none');
                setDate('');
              }}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setMode('none');
                  setDate('');
                }
              }}
              className={cardClass(mode === 'none')}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    mode === 'none' ? 'border-primary' : 'border-border'
                  }`}
                >
                  {mode === 'none' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-sans font-medium text-text-primary">
                  Not scheduled yet
                </span>
              </div>
            </div>
          </div>

          {error && <ErrorAlert error={error} className="mt-4" />}

          <div className="mt-4 flex gap-3">
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <div className="flex-1">
              <Button fullWidth onClick={save} disabled={!canSave} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
